import styles from 'LandingSite/layout/Home/Home.module.scss'
import {Helmet} from "react-helmet"
import Button from "components/Button/Buttons";
import heroI from "images/home/contract_exchange.svg"
import {Icons} from "utils/Icons";
import createImg from "images/home/feature_workflow.svg"
import commentImg from "images/home/feature_collaboration.svg"
import signingImg from "images/home/feature_signing.svg"
import {useNavigation} from "hooks/Navigation";
import IconListItem from "components/IconListItem/IconListItem";
import React from "react";
import {userStore} from "stores";
import {observer} from "mobx-react";
import Hero from "LandingSite/Hero";

const Home = observer(() => {

    const navigation = useNavigation();

    const renderHero = () => {
        return (
            <Hero heading={"Manage your legal relationships."}
                  subHeading={"Create, sign and manage the contracts your business runs on — proper legal cover, in plain English, without the lawyer's bill."}
                  imgSrc={heroI}
                  showForm
                  imgAlt={'illustration of a sealed contract with collaboration and signing'}
            >
                <div className={styles.checkList}>
                    <IconListItem icon={Icons.check()} text={"Custom contracts built around how you actually work"}/>
                    <IconListItem icon={Icons.check()} text={"Legally-sound e-signatures, built in"}/>
                    <IconListItem icon={Icons.check()} text={"Free to get started — no card required"}/>
                    <br/>
                    <Button buttonId={'home-hero-create-account'} class={styles.homeButton}
                            onClick={() => {
                                !userStore.isUser() ? navigation.goTo('/register') : navigation.goToUserPath('dashboard')
                            }}
                            title={!userStore.isUser() ? 'Get started' : 'Go to my account'}/>
                </div>
            </Hero>
        );
    };

    const renderSummaryCard = (card) => {
        return (
            <div className={styles.summaryCard}>
                <div className={styles.summaryIcon}>{card.icon}</div>
                <h3 className={styles.summaryTitle}>{card.title}</h3>
                <span className={styles.summaryContent}>{card.content}</span>
            </div>
        )
    }

    const renderSummaries = () => {
        const create = {
            icon: Icons.plus(styles.createIcon),
            title: 'Create',
            content: 'Answer a few plain-English questions and get a solid, tailored contract — no templates to wrestle with, no clauses to second-guess.'
        }
        const communicate = {
            icon: Icons.comments(styles.commentIcon),
            title: 'Collaborate',
            content: 'Share, comment and agree terms with everyone involved — colleagues, counterparties and their advisors — all in one thread.'
        }
        const sign = {
            icon: Icons.sign(styles.signIcon),
            title: 'Sign',
            content: 'Send for signature and get it back sealed and legally binding, without bouncing between third-party tools.'
        }
        const manage = {
            icon: Icons.contractStack(styles.contractIcon),
            title: 'Manage',
            content: 'Keep every signed contract, renewal date and party in one dashboard — so nothing important slips through the cracks.'
        }
        return <div className={styles.summaries}>
            <div className={styles.summariesIntro}>
                <span className={styles.eyebrow} style={{color: '#C67A00'}}>The whole contract lifecycle</span>
                <h2>One place to create, agree and keep every contract</h2>
                <p>From first draft to final signature — and everything that comes after.</p>
            </div>
            <div className={styles.summaryGrid}>
                {renderSummaryCard(create)}
                {renderSummaryCard(communicate)}
                {renderSummaryCard(sign)}
                {renderSummaryCard(manage)}
            </div>
        </div>
    }

    const renderPoints = (points, color) => (
        <div className={styles.featurePoints} style={color ? {color} : undefined}>
            {points.map((p, i) => <IconListItem key={i} icon={Icons.check()} text={p}/>)}
        </div>
    )

    const renderCreation = (feature) => {
        return (
            <div className={styles.section}>
                <div className={styles.sectionImageContainer}>
                    <img className={styles.sectionImage} src={feature.image}
                         alt='illustration of the automated workflow builder' loading="lazy"/>
                </div>
                <div className={styles.textSection}>
                    <span className={styles.eyebrow} style={{color: feature.eyebrowColor}}>{feature.eyebrow}</span>
                    <h1>{feature.title}</h1>
                    <p>{feature.content}</p>
                    {renderPoints(feature.points, feature.textColor)}
                </div>
            </div>
        )
    }

    const renderComment = (feature) => {
        return (
            <div className={styles.commentSection}>
                <div className={styles.sectionImageContainer}>
                    <img className={styles.sectionImage} src={feature.image}
                         alt='illustration of threaded comments and in-document editing' loading="lazy"/>
                </div>
                <div className={styles.textSection}>
                    <span className={styles.eyebrow} style={{color: feature.eyebrowColor}}>{feature.eyebrow}</span>
                    <h1>{feature.title}</h1>
                    <p style={{color: '#74757B'}}>{feature.content}</p>
                    {renderPoints(feature.points, feature.textColor)}
                </div>
            </div>
        )
    }

    const renderSigning = (feature) => {
        return (
            <div style={{backgroundColor: '#58595B'}} className={styles.section}>
                <div className={styles.sectionImageContainer}>
                    <img className={styles.sectionImage} src={feature.image}
                         alt='illustration of a contract being signed with a seal and security shield' loading="lazy"/>
                </div>
                <div className={styles.textSection}>
                    <span className={styles.eyebrow} style={{color: feature.eyebrowColor}}>{feature.eyebrow}</span>
                    <h1 style={{color: '#fbfbfd'}}>{feature.title}</h1>
                    <p style={{color: '#fbfbfd'}}>{feature.content}</p>
                    {renderPoints(feature.points, '#fbfbfd')}
                </div>
            </div>
        )
    }

    const renderFeatures = () => {
        const creation = {
            eyebrow: 'Create',
            eyebrowColor: '#FBB024',
            textColor: '#FBFBFD',
            title: 'Contracts that build themselves',
            content: 'Tell us what you need and Acculex guides you through it, step by step. It works out which clauses, approvals and stages your agreement needs — and the order they happen in — so you get a watertight contract without a law degree.',
            points: [
                'Guided, question-led drafting',
                'The right clauses added automatically',
                'Every stage tracked from draft to done',
            ],
            image: createImg
        }
        const commenting = {
            eyebrow: 'Collaborate',
            eyebrowColor: '#2B4CC0',
            textColor: '#3B4152',
            title: 'Get everyone on the same page. Literally.',
            content: 'Thrash out the fine print where the contract actually lives — no more email chains lost in someone’s inbox. Comment, reply in threads and loop in the people who matter, then tailor the wording yourself with built-in editing when a deal needs something bespoke.',
            points: [
                'Threaded comments with everyone involved',
                'Add or edit clauses and chapters yourself',
                'Connect your team’s messaging tools (coming soon)',
            ],
            image: commentImg
        }
        const signing = {
            eyebrow: 'Sign',
            eyebrowColor: '#FBB024',
            textColor: '#FBFBFD',
            title: 'Sign it off. Make it binding.',
            content: 'Send for signature in a click and get back a sealed, legally-binding contract — with a tamper-proof seal, a full audit trail and a certificate of completion. It all happens inside Acculex, so there’s no third-party tool to pay for and nothing leaves your control.',
            points: [
                'Legally-binding e-signatures included',
                'Tamper-proof seal and full audit trail',
                'No extra tools, no extra fees',
            ],
            image: signingImg
        }

        return (
            <div className={styles.features}>
                {renderCreation(creation)}
                {renderComment(commenting)}
                {renderSigning(signing)}
            </div>
        )
    };


    return (
        <>
            <Helmet>
                <title>Acculex | Legal Relationships and Contract Management</title>
                <meta name='description' content='Create, sign and manage the contracts your business runs on — proper legal cover in plain English, without the lawyer’s bill. Free to get started, no card required.'/>
            </Helmet>
            <div className={styles.homeContents}>
                {renderHero()}
                {renderSummaries()}
                {renderFeatures()}
                <div className={styles.signUp}>
                    <h2>Get your first contract done today</h2>
                    {Icons.arrow(styles.arrow)}
                    <Button style={{marginRight: 20}} buttonId={'home-section-create-account'}
                            title={'Get started free'}
                            onClick={() => navigation.goTo('register')}
                    />
                    <h5>Free to get started — no card, no catch</h5>
                </div>
            </div>
        </>
    );
})

export default Home
